import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle } from 'lucide-react';
import { cn } from '@siliconx/shared/ui/utils';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import {
  chatDisabledReason,
  parseChatText,
  stripLinks,
  TALK_TO_PERSON_MESSAGE,
  turnStatusNote,
  type ChatMessage,
  type ChatPayload,
  type InlineSeg,
  type TurnReply,
} from '../../lib/importChat';
import { describeChange, type Proposal } from '../../lib/importReview';

/**
 * Import assistant chat (spec 6.2). The assistant can only PROPOSE: a fix appears as a
 * card with the same Apply / Dismiss as the page, and nothing changes until an admin
 * presses Apply. Members see the transcript read-only. Model text is rendered as plain
 * text (parseChatText: no HTML, no links, no images); the assistant never supplies a link.
 */

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    await throwIfNotOk(res);
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

function Inline({ segs }: { segs: InlineSeg[] }) {
  return (
    <>
      {segs.map((s, i) =>
        s.kind === 'bold' ? (
          <strong key={i}>{s.text}</strong>
        ) : s.kind === 'code' ? (
          <code key={i} className="rounded bg-muted px-1 text-[0.85em]">
            {s.text}
          </code>
        ) : (
          <span key={i}>{s.text}</span>
        ),
      )}
    </>
  );
}

/** Safe renderer for assistant text. Exported for tests. */
export function ChatText({ text }: { text: string }) {
  const blocks = parseChatText(text);
  return (
    <div className="space-y-2 text-sm">
      {blocks.map((b, i) =>
        b.kind === 'ul' ? (
          <ul key={i} className="list-disc space-y-0.5 pl-5">
            {b.items.map((it, j) => (
              <li key={j}>
                <Inline segs={it} />
              </li>
            ))}
          </ul>
        ) : (
          <p key={i}>
            <Inline segs={b.segs} />
          </p>
        ),
      )}
    </div>
  );
}

function ProposalCard({
  proposal,
  canAct,
  busy,
  onApply,
  onDismiss,
}: {
  proposal: Proposal;
  canAct: boolean;
  busy: boolean;
  onApply: (id: string) => void;
  onDismiss: (id: string) => void;
}) {
  return (
    <div data-testid="chat-proposal" data-status={proposal.status} className="mt-2 rounded-md border border-border bg-background p-3 text-sm">
      <p className="font-medium text-foreground">Suggested fix{proposal.rowNum !== null ? ` for row ${proposal.rowNum}` : ''}</p>
      <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
        {proposal.changes.map((c, i) => (
          <li key={i}>{describeChange(c)}</li>
        ))}
      </ul>
      {proposal.reason && <p className="mt-1 text-xs text-muted-foreground">{stripLinks(proposal.reason)}</p>}
      {proposal.status === 'proposed' ? (
        canAct && (
          <div className="mt-2 flex gap-2">
            <Button size="sm" disabled={busy} onClick={() => onApply(proposal.id)}>
              Apply
            </Button>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => onDismiss(proposal.id)}>
              Dismiss
            </Button>
          </div>
        )
      ) : (
        <p className="mt-1 text-xs capitalize text-muted-foreground">{proposal.status}</p>
      )}
    </div>
  );
}

export function ChatPanel({
  batchId,
  isAdmin,
  locked,
  escalatedAt,
  busy,
  onApply,
  onDismiss,
}: {
  batchId: string;
  isAdmin: boolean;
  locked: boolean;
  escalatedAt: string | null;
  busy: boolean;
  onApply: (proposalId: string) => void;
  onDismiss: (proposalId: string) => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const chatQ = useQuery({
    queryKey: qk.importBatch.chat(batchId),
    enabled: open,
    queryFn: async () => json<ChatPayload>(await api.api.client.import.batches[':batchId'].chat.$get({ param: { batchId }, query: {} })),
  });
  const send = useMutation({
    mutationFn: async (message: string) => json<TurnReply>(await api.api.client.import.batches[':batchId'].chat.$post({ param: { batchId }, json: { message } })),
    onSuccess: (res) => {
      setNote(turnStatusNote(res.status));
      void qc.invalidateQueries({ queryKey: qk.importBatch.all(batchId) });
    },
    onError: (e: unknown) => setNote((e as Error).message),
  });

  const data = chatQ.data;
  const flag = escalatedAt ?? data?.escalatedAt ?? null;
  const reason = chatDisabledReason({
    isAdmin,
    locked,
    assistantEnabled: data?.assistantEnabled ?? true,
    turnsUsed: data?.turnsUsed ?? 0,
    turnsMax: data?.turnsMax ?? 40,
  });
  const proposalsById = new Map<string, Proposal>((data?.proposals ?? []).map((p) => [p.id, p]));
  const canSend = reason === null && !send.isPending && draft.trim().length > 0;

  const submit = (text: string) => {
    const message = text.trim();
    if (!message || send.isPending) return;
    setNote(null);
    setDraft('');
    send.mutate(message);
  };

  return (
    <Card padding="md" aria-label="Import assistant" data-testid="chat-panel">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <MessageCircle className="size-4" aria-hidden="true" />
          Ask the import assistant
        </h2>
        <Button size="sm" variant="secondary" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? 'Hide' : 'Open'}
        </Button>
      </div>
      {flag && (
        <p data-testid="chat-escalated" className="mt-2 rounded-md border border-status-green/30 bg-status-green/10 px-3 py-2 text-xs text-status-green">
          Support has been notified. The SiliconX team will follow up.
        </p>
      )}

      {open && (
        <div className="mt-3 space-y-3">
          <div className="max-h-96 space-y-3 overflow-y-auto" aria-live="polite" data-testid="chat-messages">
            {chatQ.isLoading && <p className="text-xs text-muted-foreground">Loading...</p>}
            {chatQ.error && (
              <p role="alert" className="text-xs text-status-red">
                {(chatQ.error as Error).message}
              </p>
            )}
            {data && data.messages.length === 0 && !send.isPending && (
              <p className="text-xs text-muted-foreground">Ask about the problems in your file, for example "why were rows rejected?".</p>
            )}
            {(data?.messages ?? []).map((m: ChatMessage) => (
              <div key={m.id} data-testid="chat-message" data-role={m.role} className={cn('rounded-md px-3 py-2', m.role === 'user' ? 'ml-8 bg-brand-50' : 'mr-8 bg-muted')}>
                {m.role === 'user' ? <p className="whitespace-pre-wrap text-sm">{m.text}</p> : <ChatText text={m.text} />}
                {m.proposalIds.map((id) => {
                  const p = proposalsById.get(id);
                  return p ? (
                    <ProposalCard key={id} proposal={p} canAct={isAdmin && !locked} busy={busy || send.isPending} onApply={onApply} onDismiss={onDismiss} />
                  ) : null;
                })}
              </div>
            ))}
            {send.isPending && (
              <div className="space-y-2">
                <p className="ml-8 whitespace-pre-wrap rounded-md bg-brand-50 px-3 py-2 text-sm">{send.variables}</p>
                <p data-testid="chat-typing" className="mr-8 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
                  Thinking...
                </p>
              </div>
            )}
          </div>

          {note && (
            <p role="status" className="text-xs text-status-amber">
              {note}
            </p>
          )}
          {reason ? (
            <p data-testid="chat-disabled" className="text-xs text-muted-foreground">
              {reason}
            </p>
          ) : (
            <form
              className="space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
                submit(draft);
              }}
            >
              <label className="sr-only" htmlFor="import-chat-input">
                Message the import assistant
              </label>
              <textarea
                id="import-chat-input"
                value={draft}
                maxLength={2000}
                rows={2}
                onChange={(e) => setDraft(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                placeholder="Ask about your file..."
              />
              <div className="flex items-center justify-between gap-2">
                <Button type="button" size="sm" variant="secondary" disabled={send.isPending || !!flag} onClick={() => submit(TALK_TO_PERSON_MESSAGE)}>
                  Talk to a person
                </Button>
                <Button type="submit" size="sm" disabled={!canSend}>
                  Send
                </Button>
              </div>
              {data && (
                <p className="text-right text-xs text-muted-foreground">
                  {data.turnsUsed} of {data.turnsMax} messages used
                </p>
              )}
            </form>
          )}
        </div>
      )}
    </Card>
  );
}
